import React, { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import {
  collection,
  getDocs,
  query,
  orderBy,
  where,
  doc,
  setDoc,
  deleteDoc,
  getDocs as getFirestoreDocs,
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { db } from "../firebase";
import "./Agenda.css";

function Agenda() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedEventId, setExpandedEventId] = useState(null);
  const [user, setUser] = useState(null);
  const [savedEvents, setSavedEvents] = useState({});
  const [savingEvent, setSavingEvent] = useState(null);
  const [hasScrolled, setHasScrolled] = useState(false);

  const location = useLocation();
  const params = useParams();

  useEffect(() => {
    // Check for eventId in query parameters (e.g., /agenda?event=eventId123)
    const searchParams = new URLSearchParams(location.search);
    const eventIdFromUrl = searchParams.get("event") || params.eventId;

    if (eventIdFromUrl && events.length > 0 && !loading) {
      // Check if the event exists
      const eventExists = events.find((event) => event.id === eventIdFromUrl);
      if (eventExists) {
        setExpandedEventId(eventIdFromUrl);

        // Scroll to the event after a short delay to ensure DOM is rendered
        setTimeout(() => {
          scrollToEvent(eventIdFromUrl);
        }, 100);
      }
    }
  }, [events, loading, location.search, params.eventId]);

  const scrollToEvent = (eventId) => {
    if (hasScrolled) return; // Prevent multiple scrolls

    const eventElement = document.getElementById(`event-${eventId}`);
    if (eventElement) {
      eventElement.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      setHasScrolled(true);
    }
  };

  useEffect(() => {
    // Check authentication state
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        fetchSavedEvents(currentUser.uid);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchSavedEvents = async (userId) => {
    try {
      const savedEventsRef = collection(db, "userSavedEvents");
      const q = query(savedEventsRef, where("userId", "==", userId));

      const querySnapshot = await getFirestoreDocs(q);
      const savedEventsMap = {};

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        savedEventsMap[data.eventId] = {
          savedId: doc.id,
          savedAt: data.savedAt,
        };
      });

      setSavedEvents(savedEventsMap);
    } catch (err) {
      console.error("Error fetching saved events:", err);
    }
  };

  const fetchEvents = async () => {
    try {
      setLoading(true);
      setError(null);

      const today = new Date();
      const todayStr = today.toISOString().split("T")[0];

      const eventsRef = collection(db, "events");
      const q = query(
        eventsRef,
        where("date", ">=", todayStr),
        orderBy("date"),
      );

      const querySnapshot = await getDocs(q);

      const eventsList = [];
      querySnapshot.forEach((doc) => {
        eventsList.push({
          id: doc.id,
          ...doc.data(),
        });
      });

      setEvents(eventsList);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching events:", err);
      setError("Failed to load events. Please try again later.");
      setLoading(false);
    }
  };

  // In Agenda.js - Update handleSaveEvent function
  const handleSaveEvent = async (event) => {
    if (!user) {
      window.location.href = "/login";
      return;
    }

    try {
      setSavingEvent(event.id);

      const isEventSaved = savedEvents[event.id];

      if (isEventSaved) {
        // Remove from saved events
        await deleteDoc(doc(db, "userSavedEvents", isEventSaved.savedId));

        // Update local state
        const updatedSavedEvents = { ...savedEvents };
        delete updatedSavedEvents[event.id];
        setSavedEvents(updatedSavedEvents);
      } else {
        // Add to saved events - NOW INCLUDING USER EMAIL
        const savedEventRef = doc(collection(db, "userSavedEvents"));
        await setDoc(savedEventRef, {
          userId: user.uid,
          userEmail: user.email, // ADD THIS LINE
          eventId: event.id,
          eventTitle: event.title,
          eventDate: event.date,
          eventTime: event.time,
          eventImage: event.imgLink || "",
          eventDescription: event.description || "",
          savedAt: new Date().toISOString(),
        });

        // Update local state
        setSavedEvents({
          ...savedEvents,
          [event.id]: {
            savedId: savedEventRef.id,
            savedAt: new Date().toISOString(),
          },
        });
      }
    } catch (err) {
      console.error("Error saving event:", err);
      alert("Error al guardar el evento. Por favor, intenta de nuevo.");
    } finally {
      setSavingEvent(null);
    }
  };

  const toggleEventExpansion = (eventId) => {
    if (expandedEventId === eventId) {
      setExpandedEventId(null);
    } else {
      setExpandedEventId(eventId);
    }
  };

  const futureEvents = events.filter((event) => {
    const eventDate = new Date(event.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return eventDate >= today;
  });

  const groupedEvents = futureEvents.reduce((groups, event) => {
    const date = new Date(event.date);
    const monthYear = date.toLocaleDateString("es-AR", {
      month: "long",
      year: "numeric",
    });

    if (!groups[monthYear]) {
      groups[monthYear] = [];
    }

    groups[monthYear].push(event);
    return groups;
  }, {});

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = { weekday: "short", month: "short", day: "numeric" };
    return date.toLocaleDateString("es-AR", options);
  };

  const formatFullDate = (dateString) => {
    const date = new Date(dateString);
    const options = {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    };
    return date.toLocaleDateString("es-AR", options);
  };

  const capitalizeWords = (str) => {
    if (!str) return "";
    return str.replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const currentAndUpcomingEvents = futureEvents.slice(0, 2);

  return (
    <div className="agenda-page">
      <div className="agenda-header">
        <div className="container">
          <h1>Agenda de eventos</h1>
          <p className="subtitle">
            Todos los eventos futuros en Espacio Paradigmas
          </p>
        </div>
      </div>

      <div className="agenda-content">
        <div className="container">
          {loading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Cargando eventos...</p>
            </div>
          ) : error ? (
            <div className="error-state">
              <p>{error}</p>
              <button onClick={fetchEvents} className="retry-btn">
                Probar de nuevo
              </button>
            </div>
          ) : futureEvents.length === 0 ? (
            <div className="empty-state">
              <p>¡No hay eventos futuros programados!</p>
              <p className="empty-subtext">
                Vuelve pronto para ver nuestra agenda actualizada.
              </p>
            </div>
          ) : (
            Object.entries(groupedEvents).map(([monthYear, monthEvents]) => (
              <div key={monthYear} className="month-section">
                <h2 className="month-title">{capitalizeWords(monthYear)}</h2>

                <div className="events-list">
                  {monthEvents.map((event) => {
                    const isExpanded = expandedEventId === event.id;
                    const hasImage =
                      event.imgLink && event.imgLink.trim() !== "";
                    const isEventSaved = savedEvents[event.id];

                    return (
                      <div
                        key={event.id}
                        id={`event-${event.id}`} // Add this ID
                        className={`event-item ${isExpanded ? "expanded" : ""} ${
                          hasImage ? "has-image" : ""
                        }`}
                        onClick={() => toggleEventExpansion(event.id)}
                      >
                        <div className="event-date">
                          <div className="date-box">
                            <span className="date-day">
                              {new Date(event.date).getDate()}
                            </span>
                            <span className="date-month">
                              {new Date(event.date).toLocaleDateString(
                                "es-AR",
                                {
                                  month: "short",
                                },
                              )}
                            </span>
                          </div>
                        </div>

                        <div className="event-details">
                          <div className="event-header">
                            <div className="event-title-wrapper">
                              <h3>{event.title}</h3>
                              {hasImage && !isExpanded && (
                                <span className="image-indicator"></span>
                              )}
                            </div>
                            <span className="expand-icon">
                              {isExpanded ? "−" : "+"}
                            </span>
                          </div>

                          <div className="event-meta">
                            <span className="event-time">
                              {formatDate(event.date)} • {event.time}
                            </span>
                          </div>

                          <div className="collapsed-content">
                            {event.description && (
                              <p className="event-description preview">
                                {event.description.length > 100
                                  ? `${event.description.substring(0, 100)}...`
                                  : event.description}
                              </p>
                            )}

                            {hasImage && !isExpanded && (
                              <div className="event-image-preview">
                                <img
                                  src={event.imgLink}
                                  alt={event.title}
                                  className="preview-image"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    window.open(event.imgLink, "_blank");
                                  }}
                                />
                              </div>
                            )}
                          </div>

                          {/* Expanded view (only when expanded) */}
                          {isExpanded && (
                            <div className="expanded-content">
                              {event.description &&
                                event.description.length > 100 && (
                                  <p className="event-description full">
                                    {event.description}
                                  </p>
                                )}

                              {(event.cost ||
                                event.duration ||
                                event.website) && (
                                <div className="event-details-grid">
                                  {event.duration && (
                                    <div className="detail-item">
                                      <span className="detail-label">
                                        Duración:
                                      </span>
                                      <span className="detail-value">
                                        {event.duration}
                                      </span>
                                    </div>
                                  )}

                                  {event.cost && (
                                    <div className="detail-item">
                                      <span className="detail-label">
                                        Costo:
                                      </span>
                                      <span className="detail-value">
                                        {event.cost}
                                      </span>
                                    </div>
                                  )}

                                  {event.website && (
                                    <div className="detail-item">
                                      <span className="detail-label">
                                        Enlace:
                                      </span>
                                      <a
                                        href={event.website}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="detail-value website-link"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        Ver más información
                                      </a>
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* Expanded image */}
                              {hasImage && (
                                <div className="event-image-expanded">
                                  <img
                                    src={event.imgLink}
                                    alt={event.title}
                                    className="expanded-image"
                                  />
                                </div>
                              )}

                              {/* Action buttons */}
                              <div className="event-actions">
                                <div className="action-buttons-row">
                                  {event.website && (
                                    <button
                                      className="ticket-btn"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        window.open(
                                          event.website,
                                          "_blank",
                                          "noopener,noreferrer",
                                        );
                                      }}
                                    >
                                      Comprar entrada
                                    </button>
                                  )}

                                  {/* Save Event Button - Only show in expanded view */}
                                  <button
                                    className={`save-event-btn ${
                                      isEventSaved ? "saved" : ""
                                    }`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSaveEvent(event);
                                    }}
                                    disabled={savingEvent === event.id}
                                  >
                                    {savingEvent === event.id ? (
                                      <>
                                        <span className="spinner-small"></span>
                                        Procesando...
                                      </>
                                    ) : isEventSaved ? (
                                      <>
                                        <span className="icon">✓</span>
                                        Evento Guardado
                                      </>
                                    ) : (
                                      <>
                                        <span className="icon">+</span>
                                        Guardar Evento
                                      </>
                                    )}
                                  </button>
                                </div>

                                {!user && (
                                  <p className="login-hint">
                                    Inicia sesión para guardar eventos en tu
                                    perfil
                                  </p>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Current Events Highlight */}
      {!loading && !error && currentAndUpcomingEvents.length > 0 && (
        <div className="current-month">
          <div className="container">
            <h3>Próximamente...</h3>
            <div className="current-events">
              {currentAndUpcomingEvents.map((event) => (
                <div key={event.id} className="current-event">
                  <h4>{event.title}</h4>
                  <p>
                    {formatFullDate(event.date)} • {event.time}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Agenda;
